import { Box, Button, Grid } from '@mui/material';
import { UserPermission, colors } from 'remote/global';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import {
  operatorsQueryKeys,
  useOperatorsOrdersQuery,
  usePostOperatorsRidesDriversMutation,
  usePostOperatorsRidesMoveMutation,
} from 'api/queries/operators';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { DriverCandidate, VehicleCandidate } from 'utils/data/data-types';
import { useOrderEditStatus } from 'components/modals/OrderModal/WrappedOrderDetailsModal';
import SelectVehicle from './SelectVehicle';
import SelectDriver from './SelectDriver';

interface Props {
  bookingNumber: string;
  rideId: number;
}

const getIsFormDisabled = (permissions: any) => {
  switch (permissions?.ORDER) {
    case UserPermission.NO_ACCESS:
      return true;
    case UserPermission.RESTRICTED:
      return true;
    case UserPermission.READ_OWN:
      return true;
    case UserPermission.READ_ALL:
      return true;
    default:
      return false;
  }
};

function OrderDetailsEditCarrier({ bookingNumber, rideId }: Props) {
  const { t } = useTranslation();
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const [vehicleCandidate, setVehicleCandidate] = useState<VehicleCandidate | undefined>();
  const [driverCandidate, setDriverCandidate] = useState<DriverCandidate | undefined>();
  const { permissions = {}, operatorId } = userInfo ?? {};
  const isFormDisabled = getIsFormDisabled(permissions);
  const queryClient = useQueryClient();
  const { data: order, isLoading: isOrderLoading } = useOperatorsOrdersQuery(bookingNumber);
  const selectedRide = order?.rides.find((ride) => ride.id === rideId);

  const postOperatorsRidesMoveMutation = usePostOperatorsRidesMoveMutation();
  const postOperatorsRidesDriversMutation = usePostOperatorsRidesDriversMutation(
    String(selectedRide?.id)
  );

  const { isOrderCancelled, isEditDisabled } = useOrderEditStatus({
    bookingNumber,
    rideId,
  });

  if (isOrderLoading) return null;

  const handleConfirmClick = async () => {
    if (isFormDisabled) return;

    if (vehicleCandidate != null) {
      await postOperatorsRidesMoveMutation.mutateAsync({
        vehicleId: String(vehicleCandidate.id),
        rideId: String(selectedRide?.id),
      });
      setVehicleCandidate(undefined);
      toast.success(t('Toast.success'));
    }

    if (driverCandidate?.id) {
      await postOperatorsRidesDriversMutation.mutateAsync(String(driverCandidate.id));
      setDriverCandidate(undefined);
      toast.success(t('Toast.success'));
    }

    // Fetch the order data again
    await queryClient.invalidateQueries({
      queryKey: operatorsQueryKeys.order(operatorId!, bookingNumber),
    });
  };

  const getButtonLabel = () => {
    if (isOrderCancelled) return t('cancelled');
    if (isEditDisabled) return t('confirmed');
    return t('confirm');
  };

  const isDisabled = isFormDisabled || isOrderCancelled || isEditDisabled;

  return (
    <Box
      style={{
        flex: 1,
        borderTop: `1px solid ${colors.gray4}`,
        background: colors.tiffanyBlue5,
        flexDirection: 'column',
        textAlign: 'left',
        padding: '16px 42px 16px',
      }}
    >
      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <SelectVehicle
            selectedRide={selectedRide!}
            selectedVehicleCandidate={vehicleCandidate}
            setVehicleCandidate={setVehicleCandidate}
            isEditDisabled={isDisabled}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectDriver
            selectedRide={selectedRide!}
            selectedDriverCandidate={driverCandidate}
            setSelectedDriverCandidate={setDriverCandidate}
            isEditDisabled={isDisabled}
          />
        </Grid>
      </Grid>
      <Button
        variant='contained'
        disabled={isDisabled}
        sx={{ display: 'flex', marginTop: '12px', marginLeft: 'auto', padding: '10px 28px' }}
        onClick={handleConfirmClick}
      >
        {getButtonLabel()}
      </Button>
    </Box>
  );
}

export default OrderDetailsEditCarrier;
